import { Drawer } from '@/components/ui/drawer';
import Spinner from '@/components/ui/spinner';
import { Input } from '@/components/ui/input';
import {
  ToggleButton,
  ToggleButtonGroup,
} from '@/components/ui/toggle-button-group';
import { Modal, ModalBody } from '@/components/ui/modal';
import {
  Block,
  BlockTimeSetting,
  BlockType,
  MAX_QBANK_COUNT,
} from '../../../../types/Study';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowRight,
  BookOpen,
  CaretDown,
  CaretUp,
  Timer,
} from '@phosphor-icons/react';
import { TopicTreeQbankStatusMode } from '../../../../services/qbank';
import PastQBankBlock from '../../Global/PastQBankBlock';
import SkeletonLoader from '../../Global/SkeletonLoader';
import useWindowWidth from '../../../../hooks/useWindowWidth';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { capture } from '../../../../utils/analytics';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import {
  calculateTimedBlockDurationSeconds,
  formatAccommodation,
} from '../../../../utils/accommodationUtils';
import { convertSecondsToDisplayTime } from '../../../../utils/convertSecondsToDisplayTime';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { getTimedSecondsPerQuestion } from '../../../../config/examConfig';
import Tooltip from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';

type StartableQbankStatusMode = Exclude<TopicTreeQbankStatusMode, 'viewAll'>;

interface StartQBankModalProps {
  open: boolean;
  onClose: () => void;
  examName: string;
  startEventName?: string;
  defaultCount?: number;
  qbankCounts?: {
    total: number;
    new: number;
    due: number;
    scheduled: number;
    complete: number;
    inProgress?: number;
  };
  hidePreviousBlocksAccordion?: boolean;
  isSubmitting?: boolean;
  isLoadingCounts?: boolean;
  isCountsUnavailable?: boolean;
  isViewAllSubmitting?: boolean;
  isViewAllDisabled?: boolean;
  onStart: (payload: {
    count: number;
    blockType: BlockType;
    qbankStatusMode: TopicTreeQbankStatusMode;
  }) => void;
  onViewAll?: () => void;
  onViewPreviousBlocks?: () => void;
  previousBlocks?: Block[];
  isLoadingPreviousBlocks?: boolean;
  hasFetchedPreviousBlocks?: boolean;
  hasMorePreviousBlocks?: boolean;
  expandPreviousBlocksAccordion?: boolean;
  onLoadMorePreviousBlocks?: () => void;
  onReviewPreviousBlock?: (block: Block) => void;
  onReviewCompletedQuestions?: () => void;
  // Width below which the surface renders as a bottom drawer instead of a
  // centered modal. Defaults to 690px; lecture-preview flows raise it so the
  // start surface matches the lecture drawer's bottom-sheet band.
  drawerBreakpoint?: number;
}

const StartQBankModal = ({
  open,
  onClose,
  examName,
  startEventName = 'topics_qbank_start',
  defaultCount = 40,
  qbankCounts,
  hidePreviousBlocksAccordion = false,
  isSubmitting = false,
  isLoadingCounts = false,
  isCountsUnavailable = false,
  isViewAllSubmitting = false,
  isViewAllDisabled = false,
  onStart,
  onViewAll,
  onViewPreviousBlocks,
  previousBlocks = [],
  isLoadingPreviousBlocks = false,
  hasFetchedPreviousBlocks = false,
  hasMorePreviousBlocks = false,
  expandPreviousBlocksAccordion = false,
  onLoadMorePreviousBlocks,
  onReviewPreviousBlock,
  drawerBreakpoint = 690,
}: StartQBankModalProps) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  // The block is created for the active (primary) exam, whose seconds per
  // question set the timed limit (K7: 90 medical, 120 NCLEX).
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const [qbankCount, setQbankCount] = useState<number>(defaultCount);
  const [qbankCountInput, setQbankCountInput] = useState(String(defaultCount));
  const [blockType, setBlockType] = useState<BlockType>(BlockType.tutor);

  const [qbankStatusMode, setQbankStatusMode] =
    useState<StartableQbankStatusMode>('newAndDue');

  const [isStartNewExpanded, setIsStartNewExpanded] = useState(true);

  const resolvedQbankCounts = qbankCounts ?? {
    total: 0,
    new: 0,
    due: 0,
    scheduled: 0,
    complete: 0,
    inProgress: 0,
  };

  const showQbankCounts = qbankCounts != null;

  const startableQbankCountByMode = useMemo<
    Record<StartableQbankStatusMode, number>
  >(
    () => ({
      new: Math.max(0, resolvedQbankCounts.new),
      due: Math.max(0, resolvedQbankCounts.due),
      newAndDue: Math.max(0, resolvedQbankCounts.new + resolvedQbankCounts.due),
    }),
    [resolvedQbankCounts.due, resolvedQbankCounts.new]
  );

  const availableQbankCountForMode = isCountsUnavailable
    ? MAX_QBANK_COUNT
    : startableQbankCountByMode[qbankStatusMode];

  const hasStartableQuestions =
    isCountsUnavailable || startableQbankCountByMode.newAndDue > 0;

  const minQbankCount = hasStartableQuestions ? 1 : 0;

  const maxQbankCount = hasStartableQuestions
    ? Math.min(MAX_QBANK_COUNT, availableQbankCountForMode)
    : 0;

  const clampedQbankCount = hasStartableQuestions
    ? Math.max(1, Math.min(maxQbankCount, qbankCount))
    : 0;

  const preferredBlockTimeSetting =
    currentUser?.preferredBlockTimeSetting ?? BlockTimeSetting.default;

  const timedDuration = convertSecondsToDisplayTime(
    calculateTimedBlockDurationSeconds(
      clampedQbankCount,
      preferredBlockTimeSetting,
      getTimedSecondsPerQuestion(primaryExam?.id)
    )
  );

  const formattedAccommodation = formatAccommodation(
    preferredBlockTimeSetting,
    true
  );

  const windowWidth = useWindowWidth();
  // Render as a bottom drawer below the breakpoint, a centered modal at/above.
  const isDrawer = windowWidth < drawerBreakpoint;

  const inProgressBlocks = useMemo(
    () => previousBlocks.filter((block) => block.isSubmitted === false),
    [previousBlocks]
  );

  const completedBlocks = useMemo(
    () => previousBlocks.filter((block) => block.isSubmitted !== false),
    [previousBlocks]
  );

  const hasInProgressBlocks = inProgressBlocks.length > 0;

  // Previous blocks are fetched when the modal opens; until they resolve we
  // don't know whether to surface the in-progress / completed sections.
  const isResolvingPreviousBlocks =
    open &&
    !hidePreviousBlocksAccordion &&
    Boolean(onViewPreviousBlocks) &&
    !hasFetchedPreviousBlocks;

  // The qbank counts payload carries an in-progress block count, which lets us
  // pick the "continue a block" layout before the (later-arriving) block list
  // resolves — avoiding a default→continue layout shift. Once the blocks are
  // fetched we trust the actual list; until then we trust the count hint.
  const MAX_IN_PROGRESS_SKELETONS = 5;

  const expectedInProgressCount = hasFetchedPreviousBlocks
    ? inProgressBlocks.length
    : Math.max(0, qbankCounts?.inProgress ?? 0);

  const hasExpectedInProgressBlocks = expectedInProgressCount > 0;

  const inProgressSkeletonCount = Math.min(
    Math.max(expectedInProgressCount, 1),
    MAX_IN_PROGRESS_SKELETONS
  );

  // Modal is still resolving its data (counts and/or previous blocks). We keep
  // the real layout mounted throughout and gate controls on this instead of
  // swapping the whole body for a skeleton, which caused a large layout shift.
  const isResolvingModalData = isLoadingCounts || isResolvingPreviousBlocks;

  useEffect(() => {
    if (!open) return;
    const nextStatusMode: StartableQbankStatusMode = 'newAndDue';

    const nextCount = isCountsUnavailable
      ? Math.min(defaultCount, MAX_QBANK_COUNT)
      : startableQbankCountByMode[nextStatusMode] > 0
        ? Math.min(defaultCount, startableQbankCountByMode[nextStatusMode])
        : 0;

    setQbankCount(nextCount);
    setQbankCountInput(String(nextCount));
    setBlockType(BlockType.tutor);
    setQbankStatusMode(nextStatusMode);
  }, [defaultCount, isCountsUnavailable, open, startableQbankCountByMode]);

  useEffect(() => {
    if (!open) return;
    setIsStartNewExpanded(!hasExpectedInProgressBlocks);
  }, [hasExpectedInProgressBlocks, open]);

  useEffect(() => {
    if (!open) return;

    const nextCount =
      availableQbankCountForMode > 0
        ? Math.min(qbankCount, availableQbankCountForMode)
        : 0;

    // A field mid-edit ('' or a transient "0") must not be rewritten under
    // the user's cursor — blur normalizes it. The count itself still shrinks
    // if the availability ceiling dropped.
    const isInputMidEdit =
      qbankCountInput === '' || Number(qbankCountInput) < 1;

    if (
      nextCount !== qbankCount ||
      (!isInputMidEdit && qbankCountInput !== String(nextCount))
    ) {
      setQbankCount(nextCount);
      if (!isInputMidEdit) {
        setQbankCountInput(String(nextCount));
      }
    }
  }, [
    availableQbankCountForMode,
    open,
    qbankCount,
    qbankCountInput,
    qbankStatusMode,
  ]);

  const noCompletedBlocks =
    hasFetchedPreviousBlocks && completedBlocks.length === 0;

  const disableViewCompleted =
    hidePreviousBlocksAccordion ||
    !onViewPreviousBlocks ||
    !hasFetchedPreviousBlocks ||
    noCompletedBlocks;

  const shouldShowStartNewSection =
    isStartNewExpanded ||
    !hasExpectedInProgressBlocks ||
    !hasStartableQuestions;

  const shouldAutoShowCompletedBlocks =
    !hasInProgressBlocks && !hasStartableQuestions;

  const shouldShowCompletedBlocks =
    !hidePreviousBlocksAccordion &&
    (Boolean(expandPreviousBlocksAccordion) || shouldAutoShowCompletedBlocks);

  // Mirrors the Start button's disabled state so the form's Enter-to-submit
  // path cannot start a block in a mode with no questions.
  const isStartBlocked =
    isSubmitting ||
    isResolvingModalData ||
    !hasStartableQuestions ||
    availableQbankCountForMode === 0;

  const handleStartBlock = () => {
    if (isStartBlocked) return;
    const clampedCount = Math.max(1, Math.min(maxQbankCount, qbankCount));
    if (clampedCount !== qbankCount) {
      setQbankCount(clampedCount);
      setQbankCountInput(String(clampedCount));
    }
    hapticsImpactLight();
    capture(startEventName, { examName });
    onStart({ count: clampedCount, blockType, qbankStatusMode });
  };

  const clampQbankCount = (numericValue: number) =>
    Math.max(minQbankCount, Math.min(maxQbankCount, numericValue));

  const handleQbankCountChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const digitsOnly = event.target.value.replace(/\D+/g, '');
    const normalized = digitsOnly.replace(/^0+(?=\d)/, '');

    if (normalized === '') {
      setQbankCountInput('');
      return;
    }

    const numericValue = Number(normalized);
    if (Number.isNaN(numericValue)) {
      return;
    }

    // A transient "0" (e.g. after deleting the "4" in "40") stays in the
    // field while editing; blur normalizes it. Clamping it to the minimum
    // here would rewrite the field under the user's cursor.
    if (numericValue < 1) {
      setQbankCountInput(normalized);
      return;
    }

    const clampedCount = clampQbankCount(numericValue);
    setQbankCountInput(String(clampedCount));
    setQbankCount(clampedCount);
  };

  const handleQbankCountBlur = () => {
    const trimmed = qbankCountInput.trim();
    const parsedValue = trimmed === '' ? null : Number(trimmed);

    // An emptied field restores the last valid count instead of collapsing
    // to the minimum.
    const nextCount =
      parsedValue === null || Number.isNaN(parsedValue)
        ? clampQbankCount(qbankCount)
        : clampQbankCount(parsedValue);

    setQbankCount(nextCount);
    setQbankCountInput(String(nextCount));
  };

  const handleNumericKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Tab',
      'Enter',
    ];

    if (allowedKeys.includes(event.key)) {
      if (event.key === 'Enter') {
        event.currentTarget.blur();
      }
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  };

  const handleNumericPaste = (
    event: React.ClipboardEvent<HTMLInputElement>
  ) => {
    const text = event.clipboardData.getData('text');
    if (!/^\d+$/.test(text)) {
      event.preventDefault();
    }
  };

  const controlsContext = isDrawer ? 'default' : 'modal';

  const renderStartNewConfiguration = () => {
    if (!shouldShowStartNewSection) {
      return null;
    }

    // Only surface the empty state once counts have actually resolved — while
    // counts load we keep the configuration visible with a disabled Start button.
    const showEmptyState =
      !isLoadingCounts && !isCountsUnavailable && !hasStartableQuestions;

    // While the modal is creating a block or still resolving its data, show a
    // spinner in the (disabled) Start button instead of a label.
    const isStartButtonBusy = isSubmitting || isResolvingModalData;

    return (
      <div className="start-qbank-new-block">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (isStartBlocked) {
              return;
            }

            const clampedCount = Math.max(
              1,
              Math.min(maxQbankCount, qbankCount)
            );

            if (clampedCount !== qbankCount) {
              setQbankCount(clampedCount);
              setQbankCountInput(String(clampedCount));
            }
            onStart({ count: clampedCount, blockType, qbankStatusMode });
          }}
        >
          <div className="start-qbank-controls">
            <div className="mobile-row">
              <div className="qbank-count" style={{ width: '4.75rem' }}>
                {isLoadingCounts ? (
                  <div
                    className="start-qbank-skeleton start-qbank-skeleton_input"
                    aria-label="Loading question count"
                  />
                ) : (
                  <Input
                    value={qbankCountInput}
                    type="text"
                    onChange={handleQbankCountChange}
                    onBlur={handleQbankCountBlur}
                    onKeyDown={handleNumericKeyDown}
                    onPaste={handleNumericPaste}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    min={minQbankCount}
                    max={maxQbankCount}
                    context={controlsContext}
                  />
                )}
              </div>
              <ToggleButtonGroup
                exclusive
                disabled={isLoadingCounts}
                className="block-type-toggle-button-group !min-w-[14.5rem]"
                context={controlsContext}
                value={blockType}
                onChange={(_, newValue: BlockType | null) => {
                  if (newValue) setBlockType(newValue);
                }}
              >
                <ToggleButton value="tutor" sx={{ gap: '.5rem' }}>
                  <BookOpen />
                  Untimed
                </ToggleButton>
                <ToggleButton value="timed" sx={{ gap: '.375rem' }}>
                  <Timer />
                  Timed
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
            <ToggleButtonGroup
              exclusive
              disabled={isLoadingCounts || isCountsUnavailable}
              className="qbank-status-toggle-button-group"
              context={controlsContext}
              value={qbankStatusMode}
              onChange={(_, newValue: StartableQbankStatusMode | null) => {
                if (newValue) setQbankStatusMode(newValue);
              }}
            >
              <ToggleButton
                value="new"
                disabled={
                  !isCountsUnavailable && startableQbankCountByMode.new === 0
                }
              >
                New
              </ToggleButton>
              <ToggleButton
                value="due"
                disabled={
                  !isCountsUnavailable && startableQbankCountByMode.due === 0
                }
              >
                Due
              </ToggleButton>
              <ToggleButton
                value="newAndDue"
                disabled={
                  !isCountsUnavailable &&
                  startableQbankCountByMode.newAndDue === 0
                }
              >
                New & Due
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
        </form>
        {blockType === BlockType.timed &&
          hasStartableQuestions &&
          !isLoadingCounts && (
            <div className="start-modal-timed-summary">
              <p>
                Block Time: {timedDuration}
                {formattedAccommodation
                  ? ` (${formattedAccommodation} Time)`
                  : ''}
              </p>
            </div>
          )}
        {showEmptyState && (
          <div className="start-qbank-empty">
            <p className="m-y-0">
              No new or due QBank questions are available right now.
            </p>
          </div>
        )}
        {!showEmptyState && (
          <button
            className="button"
            type="button"
            onClick={handleStartBlock}
            disabled={isStartButtonBusy || availableQbankCountForMode === 0}
          >
            {isStartButtonBusy ? (
              <Spinner
                size={16}
                style={{ color: 'currentColor' }}
                aria-label={isSubmitting ? 'Starting block' : 'Loading'}
              />
            ) : (
              'Start Block'
            )}
          </button>
        )}
        {isCountsUnavailable && (
          <p className="m-y-0 text-sm text-fg-secondary">
            Personal counts are unavailable. Starting a block will select
            eligible questions for this topic.
          </p>
        )}
      </div>
    );
  };

  const renderCompletedBlocks = () => {
    if (!shouldShowCompletedBlocks) {
      return null;
    }

    return (
      <div className="start-qbank-previous">
        <h3>Completed Blocks</h3>
        {completedBlocks.length === 0 && !isLoadingPreviousBlocks && (
          <p className="start-qbank-previous_empty">No completed blocks yet.</p>
        )}
        {isLoadingPreviousBlocks && completedBlocks.length === 0 && (
          <p className="start-qbank-previous_loading">Loading blocks...</p>
        )}
        {completedBlocks.map((block) => {
          return (
            <PastQBankBlock
              key={block.id}
              block={block}
              reviewBlockFunction={() => {
                onReviewPreviousBlock?.(block);
              }}
            />
          );
        })}
        {isLoadingPreviousBlocks && completedBlocks.length > 0 && (
          <p className="start-qbank-previous_loading">Loading blocks...</p>
        )}
        {hasMorePreviousBlocks &&
          !isLoadingPreviousBlocks &&
          completedBlocks.length > 0 && (
            <button
              className="button button--see-more button--link"
              type="button"
              onClick={() => {
                onLoadMorePreviousBlocks?.();
                hapticsImpactLight();
              }}
              tabIndex={0}
              aria-label="See more completed blocks"
            >
              See More
            </button>
          )}
      </div>
    );
  };

  // Placeholder for the in-progress section while the block list is still
  // loading but the count hint tells us blocks exist. Reserves the layout so
  // the real cards drop into place without shifting the configuration below.
  const renderInProgressSkeleton = () => {
    return (
      <div className="start-qbank-in-progress" aria-label="Loading blocks">
        <div className="start-qbank-section-header">
          <h3>
            {inProgressSkeletonCount === 1
              ? 'Pick Up Where You Left Off'
              : 'Continue a Block in Progress'}
          </h3>
        </div>
        {Array.from({ length: inProgressSkeletonCount }).map((_, index) => (
          <div
            key={index}
            className="past-qbank-block past-qbank-block--skeleton"
            aria-hidden="true"
          >
            <div className="past-qbank-block_body">
              <div className="left">
                <SkeletonLoader width="45%" height="0.75rem" />
                <SkeletonLoader width="75%" height="1.75rem" />
                <SkeletonLoader width="60%" height="1rem" />
                <SkeletonLoader width="52%" height="1rem" />
                <SkeletonLoader width="48%" height="1rem" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderModalContent = () => {
    return (
      <ModalBody className="modal_content">
        <div className="start-modal-header">
          <div className="start-modal-header_title">
            <h2>{examName}</h2>
            {onViewAll && (
              <button
                className="button button--link"
                type="button"
                onClick={() => {
                  if (isViewAllSubmitting || isViewAllDisabled) return;
                  hapticsImpactLight();
                  onViewAll();
                }}
                disabled={isViewAllSubmitting || isViewAllDisabled}
              >
                View All Questions <ArrowRight />
              </button>
            )}
          </div>
          {(showQbankCounts || isLoadingCounts) && (
            <div
              className="start-modal-badges"
              aria-label={
                isLoadingCounts ? 'Loading QBank counts' : 'QBank counts'
              }
            >
              <Tooltip
                title="Unseen questions."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {isLoadingCounts ? (
                      <span
                        className="start-qbank-skeleton start-qbank-skeleton_badge-value"
                        aria-hidden="true"
                      />
                    ) : (
                      resolvedQbankCounts.new
                    )}
                  </span>{' '}
                  New
                </span>
              </Tooltip>
              <Tooltip
                title="Previously missed questions returning today as variants."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {isLoadingCounts ? (
                      <span
                        className="start-qbank-skeleton start-qbank-skeleton_badge-value"
                        aria-hidden="true"
                      />
                    ) : (
                      resolvedQbankCounts.due
                    )}
                  </span>{' '}
                  Due
                </span>
              </Tooltip>
              <Tooltip
                title="Previously missed questions scheduled to return in the future."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {isLoadingCounts ? (
                      <span
                        className="start-qbank-skeleton start-qbank-skeleton_badge-value"
                        aria-hidden="true"
                      />
                    ) : (
                      resolvedQbankCounts.scheduled
                    )}
                  </span>{' '}
                  Scheduled
                </span>
              </Tooltip>
              <Tooltip
                title="Questions removed from review because you answered them correctly or used up every version."
                enterTouchDelay={0}
                enterDelay={250}
              >
                <span className="start-modal-badge">
                  <span className="start-modal-badge_value">
                    {isLoadingCounts ? (
                      <span
                        className="start-qbank-skeleton start-qbank-skeleton_badge-value"
                        aria-hidden="true"
                      />
                    ) : (
                      resolvedQbankCounts.complete
                    )}
                  </span>{' '}
                  Retired
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {!isResolvingPreviousBlocks && hasInProgressBlocks && (
          <div className="start-qbank-in-progress">
            <div className="start-qbank-section-header">
              <h3>
                {inProgressBlocks.length === 1
                  ? 'Pick Up Where You Left Off'
                  : 'Continue a Block in Progress'}
              </h3>
              {inProgressBlocks.length > 1 && (
                <span>{inProgressBlocks.length} in progress</span>
              )}
            </div>
            {inProgressBlocks.map((block) => (
              <PastQBankBlock
                key={block.id}
                block={block}
                reviewBlockFunction={() => {
                  onReviewPreviousBlock?.(block);
                }}
              />
            ))}
          </div>
        )}
        {isResolvingPreviousBlocks &&
          hasExpectedInProgressBlocks &&
          renderInProgressSkeleton()}
        {hasExpectedInProgressBlocks && hasStartableQuestions && (
          <Button
            className="start-qbank-start-new-toggle !p-0"
            variant="link"
            onClick={() => {
              setIsStartNewExpanded((value) => !value);
              hapticsImpactLight();
            }}
          >
            Start a New Block Instead
            {isStartNewExpanded ? (
              <CaretUp weight="fill" />
            ) : (
              <CaretDown weight="fill" />
            )}
          </Button>
        )}
        {renderStartNewConfiguration()}
        {!isResolvingPreviousBlocks &&
          !hidePreviousBlocksAccordion &&
          !shouldAutoShowCompletedBlocks &&
          !noCompletedBlocks && (
            <Button
              className="start-qbank-completed-toggle !p-0"
              variant="link"
              onClick={() => {
                if (onViewPreviousBlocks) {
                  onViewPreviousBlocks();
                  hapticsImpactLight();
                }
              }}
              disabled={disableViewCompleted}
            >
              View Completed Blocks
              {expandPreviousBlocksAccordion ? (
                <CaretUp weight="fill" />
              ) : (
                <CaretDown weight="fill" />
              )}
              {isLoadingPreviousBlocks && (
                <Spinner
                  context={!isDrawer ? 'modal' : 'default'}
                  size={14}
                  aria-label="Loading completed blocks"
                />
              )}
            </Button>
          )}
        {!isResolvingPreviousBlocks && renderCompletedBlocks()}
      </ModalBody>
    );
  };

  return (
    <>
      {!isDrawer ? (
        <Modal
          open={open}
          onClose={() => {
            (document.activeElement as HTMLElement)?.blur();
            onClose();
          }}
          name="start-qbank"
          className="modal--glass"
        >
          {renderModalContent()}
        </Modal>
      ) : (
        <Drawer
          className="modal--start-qbank start-qbank-drawer"
          aria-label="Start QBank"
          anchor="bottom"
          open={open}
          onClose={() => {
            (document.activeElement as HTMLElement)?.blur();
            onClose();
          }}
          onOpen={() => {}}
          swipeable
        >
          {renderModalContent()}
        </Drawer>
      )}
    </>
  );
};

export default StartQBankModal;
